// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { addAssetURLLink } from '@/asset/api/add-asset-url-link';
import { UpdateAssetParameters, updateAsset } from '@/asset/api/update-asset';
import { uploadAssetFile } from '@/asset/api/upload-asset-file';
import { AssetActionButton } from '@/asset/components/asset-action-button';
import { AssetErrorBoundary } from '@/asset/components/asset-error-boundary';
import { AssetEventGroupTimeline } from '@/asset/components/asset-event-group-timeline';
import { AssetPropertiesControl } from '@/asset/components/asset-properties-control';
import { AssetScrapProperties } from '@/asset/components/asset-scrap-properties';
import { AssetSerialDialog } from '@/asset/components/asset-serial-dialog';
import { useAsset } from '@/asset/hooks/use-asset';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { EditableTypography } from '@/components/editable-typography';
import { FileInputButton } from '@/components/file-input-button';
import { Link } from '@/components/link';
import { LinkInputButton } from '@/components/link-input-button';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import {
  AddIcCallOutlined,
  UploadFileOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { CreateTicketDialog } from '@/ticket/components/create-ticket-dialog';
import { AddURLLinkParams } from '@/url-link/api/types';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  assign: z.string().optional(), // Launch assignment
  unassign: z.string().optional(), // Launch unassign
  editEventGroup: z.string().optional(), // Launch AEG edit dialog
});

export const Route = createFileRoute('/_app/assets/$assetKey')({
  component: AssetRoute,
  validateSearch: searchSchema,
});

function AssetRoute() {
  const { assetKey } = Route.useParams();

  return (
    <AssetErrorBoundary>
      <AssetContent assetKey={assetKey} />
    </AssetErrorBoundary>
  );
}

function AssetContent(props: { assetKey: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const serialDialog = useDisclosure();
  const ticketDialog = useDisclosure();

  const { asset } = useAsset(props.assetKey);

  const updateAssetMutation = useMutation({
    mutationFn: (data: UpdateAssetParameters) =>
      updateAsset(props.assetKey, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: (file: globalThis.File) => uploadAssetFile(asset.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
      navigate({
        to: '/assets/$assetKey/attachments',
        params: { assetKey: props.assetKey },
      });
    },
  });

  const addURLLinkMutation = useMutation({
    mutationFn: (params: AddURLLinkParams) => addAssetURLLink(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.assetKey] });
      navigate({
        to: '/assets/$assetKey/attachments',
        params: { assetKey: props.assetKey },
      });
    },
  });

  const isScrapped = asset.functionalStatus === 'SCRAPPED';

  const handleChangeDescription = (value: string) => {
    const description = value === '' ? null : value;

    updateAssetMutation.mutate({ description });
  };

  return (
    <Stack>
      <AssetSerialDialog
        asset={asset}
        isOpen={serialDialog.isOpen}
        onClose={serialDialog.close}
      />
      <CreateTicketDialog
        isOpen={ticketDialog.isOpen}
        onClose={ticketDialog.close}
        forAssetIds={[asset]}
        onSuccess={() => {
          navigate({
            to: '/assets/$assetKey/tickets',
            params: { assetKey: props.assetKey },
          });
        }}
      />
      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={3}
          marginTop={3}
        >
          <Breadcrumbs separator="›">
            <Link
              to="/assets"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Assets
            </Link>
            <Typography variant="body2" color="text.secondary">
              {asset.serial}
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcCallOutlined fontSize="small" />}
              onClick={ticketDialog.open}
            >
              Open ticket
            </Button>
            <FileInputButton
              size="small"
              variant="outlined"
              onChange={uploadFileMutation.mutate}
              disabled={uploadFileMutation.isPending}
              startIcon={<UploadFileOutlined fontSize="small" />}
            >
              Upload file
            </FileInputButton>
            <LinkInputButton
              size="small"
              variant="outlined"
              onAddLink={(name, url) => {
                addURLLinkMutation.mutate({
                  entityKey: props.assetKey,
                  name,
                  url,
                });
              }}
              disabled={addURLLinkMutation.isPending}
            />
            <AssetActionButton asset={asset} />
          </Stack>
        </Stack>
        <Stack paddingX={{ xs: 1, md: 3 }} spacing={1}>
          <Link
            to="/catalog/items/$itemKey"
            params={{ itemKey: asset.item.key }}
            sx={{ alignSelf: 'start' }}
          >
            {asset.item.name}
          </Link>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack maxWidth="50%" flex={1} alignSelf="start">
              <EditablePropertyButton variant="h5" onClick={serialDialog.open}>
                {asset.serial}
              </EditablePropertyButton>
              <EditableTypography
                key={asset.description}
                value={asset.description}
                variant="body2"
                placeholder="Description"
                disabled={updateAssetMutation.isPending}
                onChange={handleChangeDescription}
              />
            </Stack>
            {asset.functionalStatus === 'SCRAPPED' ? (
              <AssetScrapProperties asset={asset} sx={{ marginLeft: 'auto' }} />
            ) : (
              <AssetPropertiesControl
                asset={asset}
                sx={{ marginLeft: 'auto' }}
              />
            )}
          </Stack>
        </Stack>
        <Suspense>
          <Box paddingX={{ xs: 1, md: 3 }} paddingTop={2}>
            <AssetEventGroupTimeline asset={asset} isDisabled={isScrapped} />
          </Box>
        </Suspense>
        <Stack
          spacing={2}
          marginY={3}
          paddingX={{ xs: 1, md: 3 }}
          direction={{ xs: 'column', md: 'row' }}
        >
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            <VerticalNavigationLink
              to="/assets/$assetKey"
              params={{ assetKey: props.assetKey }}
              activeOptions={{ exact: true }}
            >
              Basic info
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/assets/$assetKey/metadata"
              params={{ assetKey: props.assetKey }}
            >
              Metadata
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/assets/$assetKey/tickets"
              params={{ assetKey: props.assetKey }}
            >
              Tickets ({asset.ticketCount})
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/assets/$assetKey/attachments"
              params={{ assetKey: props.assetKey }}
            >
              Files and links ({asset.fileCount + asset.urlLinkCount || 0})
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/assets/$assetKey/accounting"
              params={{ assetKey: props.assetKey }}
            >
              Accounting
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/assets/$assetKey/audit"
              params={{ assetKey: props.assetKey }}
            >
              Audit log
            </VerticalNavigationLink>
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Container>
    </Stack>
  );
}
